import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Modal, Button, Space, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { request } from '@/utils/request';
import { loginOut } from '@/components/RightContent/AvatarDropdown';

const { Text } = Typography;

/** Show warning this many ms before the token expires */
const WARNING_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
/** How often to check the token expiry */
const CHECK_INTERVAL_MS = 10_000; // 10 seconds
/** Auto-logout if no response after this many ms once the modal is shown */
const AUTO_LOGOUT_MS = 60_000; // 60 seconds

const parseJwtExp = (token: string): number => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.exp || 0) * 1000; // convert to ms
  } catch {
    return 0;
  }
};

export const SessionExpiryWarning: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [renewing, setRenewing] = useState(false);
  const countdownRef = useRef<number | null>(null);
  const checkRef = useRef<number | null>(null);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    clearCountdown();
    setVisible(false);
    await loginOut();
  }, [clearCountdown]);

  const handleStayLoggedIn = useCallback(async () => {
    setRenewing(true);
    try {
      const response = await request<{ token: string }>('/auth/renew', {
        method: 'POST',
      });
      if (response?.token) {
        localStorage.setItem('token', response.token);
        clearCountdown();
        setVisible(false);
      }
    } catch {
      // Renewal failed (e.g. token already expired) — force logout
      await handleLogout();
    } finally {
      setRenewing(false);
    }
  }, [clearCountdown, handleLogout]);

  // Start countdown when modal becomes visible
  useEffect(() => {
    if (!visible) return;

    const seconds = Math.floor(AUTO_LOGOUT_MS / 1000);
    setCountdown(seconds);

    countdownRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return clearCountdown;
  }, [visible, clearCountdown, handleLogout]);

  // Periodically check token expiry
  useEffect(() => {
    checkRef.current = window.setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token) return;

      const expiresAt = parseJwtExp(token);
      if (!expiresAt) return;

      const timeLeft = expiresAt - Date.now();

      if (timeLeft <= 0) {
        // Already expired — logout immediately
        handleLogout();
      } else if (timeLeft <= WARNING_BEFORE_EXPIRY_MS && !visible) {
        setVisible(true);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      if (checkRef.current) {
        clearInterval(checkRef.current);
      }
    };
  }, [visible, handleLogout]);

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          Session Expiring Soon
        </Space>
      }
      open={visible}
      closable={false}
      maskClosable={false}
      footer={
        <Space>
          <Button onClick={handleLogout}>Logout</Button>
          <Button type="primary" loading={renewing} onClick={handleStayLoggedIn}>
            Stay Logged In
          </Button>
        </Space>
      }
    >
      <Text>
        Your session is about to expire. You will be automatically logged out in{' '}
        <Text strong>{countdown}</Text> seconds.
      </Text>
      <br />
      <br />
      <Text type="secondary">
        Click "Stay Logged In" to extend your session, or "Logout" to end it now.
      </Text>
    </Modal>
  );
};
